import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, MapPin, Users, Tag, Palette } from 'lucide-react';
import { EventData, useEvents } from '../contexts/EventsContext';
import { convertSaoPauloToUtc, convertUtcToDateTimeLocal } from '../utils/dateUtils';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event?: EventData | null; // null para criar, EventData para editar
}

const EventModal: React.FC<EventModalProps> = ({ isOpen, onClose, event }) => {
  const { createEvent, updateEvent } = useEvents();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    custom_label: '',
    label_color: '#0FFF55',
    event_date: '',
    duration_minutes: 60,
    participants_limit: '',
    location: '',
    event_type: 'call' // mantido para compatibilidade
  });

  // Cores predefinidas para escolha rápida
  const predefinedColors = [
    '#0FFF55', // Verde MasterClub
    '#3B82F6', // Azul
    '#F97316', // Laranja
    '#EF4444', // Vermelho
    '#8B5CF6', // Roxo
    '#F59E0B', // Amarelo
    '#10B981', // Verde escuro
    '#EC4899', // Rosa
    '#6B7280', // Cinza
    '#14B8A6', // Teal
  ];

  // Resetar form quando modal abre/fecha ou evento muda
  useEffect(() => {
    if (isOpen) {
      if (event) {
        // Editando evento existente - converter UTC do banco para São Paulo
        console.log('✏️ === EDITANDO EVENTO ===');
        console.log('📅 Data original do banco (UTC):', event.event_date);
        console.log('🕒 Data UTC parseada:', new Date(event.event_date));
        
        const dateTimeLocal = convertUtcToDateTimeLocal(event.event_date);
        
        console.log('📅 Data convertida para o form (São Paulo):', dateTimeLocal);
        console.log('🕒 Data que aparecerá no campo datetime-local:', dateTimeLocal);
        
        setFormData({
          title: event.title,
          description: event.description || '',
          custom_label: event.custom_label,
          label_color: event.label_color,
          event_date: dateTimeLocal,
          duration_minutes: event.duration_minutes,
          participants_limit: event.participants_limit?.toString() || '',
          location: event.location || '',
          event_type: event.event_type
        });
      } else {
        // Criando novo evento
        setFormData({
          title: '',
          description: '',
          custom_label: '',
          label_color: '#0FFF55',
          event_date: '',
          duration_minutes: 60,
          participants_limit: '',
          location: '',
          event_type: 'call'
        });
      }
      setError(null);
    }
  }, [isOpen, event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.custom_label.trim() || !formData.event_date) {
      setError('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Converter data de São Paulo para UTC para salvar no banco
      console.log('💾 === SALVANDO EVENTO ===');
      console.log('📅 Data do form (formato datetime-local):', formData.event_date);
      
      const utcDateString = convertSaoPauloToUtc(formData.event_date);
      
      console.log('📅 Data convertida para UTC (banco):', utcDateString);
      console.log('🕒 Horário informado pelo usuário:', formData.event_date);
      console.log('🕒 Horário que será salvo no banco:', new Date(utcDateString).toISOString());

      const eventData = {
        ...formData,
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        custom_label: formData.custom_label.trim(),
        event_date: utcDateString, // Usar a data convertida para UTC
        participants_limit: formData.participants_limit ? parseInt(formData.participants_limit) : undefined,
        location: formData.location.trim() || undefined,
        is_active: true, // Novos eventos são sempre ativos
      };

      let result;
      if (event) {
        // Atualizando evento existente
        result = await updateEvent(event.id, eventData);
      } else {
        // Criando novo evento
        result = await createEvent(eventData);
      }

      if (result.success) {
        onClose();
      } else {
        setError(result.error || 'Erro ao salvar evento');
      }
    } catch (err) {
      setError('Erro inesperado ao salvar evento');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1d21] rounded-3xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white">
            {event ? 'Editar Evento' : 'Novo Evento'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-[#2a2d31] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Título */}
          <div>
            <label className="block text-white font-medium mb-2">
              Título do Evento *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Ex: Call Semanal - Estratégias de Vendas"
              className="w-full bg-[#121316] text-white border border-gray-600 rounded-xl px-4 py-3 focus:border-[#0FFF55] focus:outline-none"
              required
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-white font-medium mb-2">
              Descrição
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Descreva o conteúdo do evento..."
              rows={3}
              className="w-full bg-[#121316] text-white border border-gray-600 rounded-xl px-4 py-3 focus:border-[#0FFF55] focus:outline-none resize-none"
            />
          </div>

          {/* Etiqueta personalizada */}
          <div>
            <label className="block text-white font-medium mb-2">
              <Tag className="inline w-4 h-4 mr-1" />
              Etiqueta *
            </label>
            <input
              type="text"
              name="custom_label"
              value={formData.custom_label}
              onChange={handleInputChange}
              placeholder="Ex: Call, Workshop, Reunião, Treinamento..."
              className="w-full bg-[#121316] text-white border border-gray-600 rounded-xl px-4 py-3 focus:border-[#0FFF55] focus:outline-none"
              required
            />
          </div>

          {/* Seletor de cor */}
          <div>
            <label className="block text-white font-medium mb-2">
              <Palette className="inline w-4 h-4 mr-1" />
              Cor da Etiqueta
            </label>
            <div className="space-y-3">
              {/* Cores predefinidas */}
              <div className="flex gap-2 flex-wrap">
                {predefinedColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, label_color: color }))}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      formData.label_color === color ? 'border-white' : 'border-gray-600'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              
              {/* Seletor de cor personalizada */}
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  name="label_color"
                  value={formData.label_color}
                  onChange={handleInputChange}
                  className="w-12 h-10 bg-[#121316] border border-gray-600 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.label_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, label_color: e.target.value }))}
                  placeholder="#0FFF55"
                  className="bg-[#121316] text-white border border-gray-600 rounded-lg px-3 py-2 focus:border-[#0FFF55] focus:outline-none flex-1"
                />
              </div>
            </div>
          </div>

          {/* Data e hora */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white font-medium mb-2">
                <Calendar className="inline w-4 h-4 mr-1 text-[#0FFF55]" />
                Data e Hora *
              </label>
                             <input
                 type="datetime-local"
                 name="event_date"
                 value={formData.event_date}
                 onChange={handleInputChange}
                 className="w-full bg-[#121316] text-white border border-gray-600 rounded-xl px-4 py-3 focus:border-[#0FFF55] focus:outline-none [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:brightness-0"
                 required
               />
            </div>

            <div>
              <label className="block text-white font-medium mb-2">
                <Clock className="inline w-4 h-4 mr-1" />
                Duração (minutos)
              </label>
              <input
                type="number"
                name="duration_minutes"
                value={formData.duration_minutes}
                onChange={handleInputChange}
                min="15"
                step="15"
                className="w-full bg-[#121316] text-white border border-gray-600 rounded-xl px-4 py-3 focus:border-[#0FFF55] focus:outline-none"
              />
            </div>
          </div>

          {/* Local e participantes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white font-medium mb-2">
                <MapPin className="inline w-4 h-4 mr-1" />
                Local/Plataforma
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Ex: Zoom, YouTube Live, Google Meet"
                className="w-full bg-[#121316] text-white border border-gray-600 rounded-xl px-4 py-3 focus:border-[#0FFF55] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2">
                <Users className="inline w-4 h-4 mr-1" />
                Limite de Participantes
              </label>
              <input
                type="number"
                name="participants_limit"
                value={formData.participants_limit}
                onChange={handleInputChange}
                min="1"
                placeholder="Deixe vazio para ilimitado"
                className="w-full bg-[#121316] text-white border border-gray-600 rounded-xl px-4 py-3 focus:border-[#0FFF55] focus:outline-none"
              />
            </div>
          </div>

          {/* Preview da etiqueta */}
          {formData.custom_label && (
            <div>
              <label className="block text-white font-medium mb-2">
                Preview da Etiqueta
              </label>
              <div className="flex items-center space-x-2">
                <span 
                  className="px-3 py-1 rounded-full text-sm font-semibold"
                  style={{ 
                    backgroundColor: formData.label_color,
                    color: formData.label_color === '#0FFF55' ? '#121316' : '#ffffff'
                  }}
                >
                  {formData.custom_label}
                </span>
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-bold px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-[#0FFF55]/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Salvando...' : event ? 'Atualizar Evento' : 'Criar Evento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;